import React, { useEffect } from 'react';
import { FieldError, FieldErrors, UseFormRegister } from 'react-hook-form';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';

type FormFieldError = FieldError | undefined | FieldErrors<any>;

interface TabOption {
  value: string;
  label: string;
}

interface TabRadioFieldProps {
  name: string;
  label?: string;
  description?: string;
  disabled?: boolean;
  register?: UseFormRegister<any>;
  error?: FormFieldError;
  className?: string;
  size?: string;
  options: TabOption[];
  defaultValue?: string;
  value?: string; // Add value prop
}

const TabRadioField: React.FC<TabRadioFieldProps> = ({
  name,
  label,
  description,
  disabled = false,
  register,
  error,
  className,
  size = '',
  options,
  defaultValue = '',
  value // Destructure value prop
}) => {
  const [selectedValue, setSelectedValue] = React.useState(value || defaultValue);

  // Sync local state with form value when it changes
  useEffect(() => {
    if (value !== undefined && value !== selectedValue) {
      setSelectedValue(value);
    }
  }, [value]);

  const handleValueChange = (value: string) => {
    setSelectedValue(value);

    // Update the hidden input for RHF
    if (register) {
      const registeredField = register(name);
      if (registeredField.onChange) {
        registeredField.onChange({
          target: { name, value },
          type: 'change'
        });
      }
    }
  };

  const errorMessage = error && typeof error === 'object' && 'message' in error ? error.message : '';

  return (
    <div className={`${className ? className : ''}`}>
      {label && (
        <label
          className={`inline-block text-[#495057] font-medium text-xs ${
            size === 'sm' ? 'md:mb-[0.45rem] md:text-sm' : 'md:text-lg mb-2'
          }`}
        >
          {label}
        </label>
      )}

      <Tabs value={selectedValue} onValueChange={handleValueChange} className="w-full">
        <TabsList
          className={`
            grid w-full grid-cols-${options.length} bg-[#DBD6FF] rounded-[8px] border
            ${size === 'sm' ? 'h-[44px] p-1' : 'h-[44px] md:h-[52px] p-1 md:p-1.5'}
            ${error ? 'border-red-500' : 'border-gray-300'}
            ${disabled ? 'opacity-75 pointer-events-none' : ''}
          `}
        >
          {options.map((option) => (
            <TabsTrigger
              key={option.value}
              value={option.value}
              disabled={disabled}
              className={`
                rounded-[8px] font-medium transition-all duration-200
                ${size === 'sm' ? 'text-base px-3 py-[5px]' : 'text-base px-4 py-2.5'}
                data-[state=active]:bg-primary data-[state=active]:text-white data-[state=active]:shadow-sm
                data-[state=inactive]:bg-transparent data-[state=inactive]:text-primary
                hover:data-[state=inactive]:text-[#495057]
                disabled:opacity-50 disabled:cursor-not-allowed
              `}
            >
              {option.label}
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      {/* Hidden input for form submission */}
      {register && <input type="hidden" {...register(name)} />}

      {/* Error message or description */}
      {(errorMessage || description) && (
        <p className={`mt-2 text-xs ${errorMessage ? 'text-red-600' : 'text-[#93999F]'}`}>
          {(errorMessage as string) || description}
        </p>
      )}
    </div>
  );
};

export default TabRadioField;
